import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { enablePush, isIosLike, isStandalone, isPushSupported } from "../lib/push";
import { trackSiteEvent } from "../lib/analytics";
import IosInstallSheet from "./IosInstallSheet";

/**
 * PushOptInPrompt — Phase B soft prompt.
 *
 * Layout: bottom sheet on mobile, bottom-right toast-style card on desktop.
 * Copy: "Want alerts when food trucks are near you?" + Enable / Not now.
 *
 * iOS Safari + non-standalone (i.e. user is in a browser tab, not a
 * home-screen PWA) cannot receive Web Push — we swap the CTA to an
 * "Add to Home Screen" coachmark instead of failing silently.
 *
 * The host (`MapView`) decides whether to render this via the
 * `usePushSoftPrompt` hook. We just take a `visible` prop and an
 * `onDismiss(reason)` callback.
 */
export default function PushOptInPrompt({ visible, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const iosNoPwa = isIosLike() && !isStandalone();

  // Fire the impression event exactly once per mount — the hook guards
  // against re-mounts within a session, but if for some reason this
  // component renders twice we still only ping once.
  useEffect(() => {
    if (!visible) return;
    trackSiteEvent("notif_prompt_shown", { ios_no_pwa: iosNoPwa });
    if (iosNoPwa) trackSiteEvent("notif_ios_install_hint");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!isPushSupported() && !iosNoPwa) {
    // Browser flat-out doesn't support push (e.g. very old) and isn't iOS.
    // Don't show anything — silent no-op is the most polite behavior.
    return null;
  }

  const handleEnable = async () => {
    if (iosNoPwa) {
      // Suppress the soft prompt while we walk the user through the
      // visual install flow. They'll come back via the home-screen icon.
      trackSiteEvent("notif_prompt_dismissed", { reason: "ios_install_hint" });
      setIosSheetOpen(true);
      onDismiss?.("not_now");
      return;
    }
    setBusy(true);
    const res = await enablePush();
    setBusy(false);
    if (res.ok) {
      trackSiteEvent("notif_prompt_accepted");
      trackSiteEvent("notif_subscribed");
      toast.success("You're in. We'll only ping you with the good stuff.");
      onDismiss?.("accepted");
    } else if (res.reason === "denied") {
      trackSiteEvent("notif_blocked");
      toast.error("Notifications blocked. You can re-enable in browser settings.");
      onDismiss?.("denied");
    } else if (res.reason === "ios_needs_pwa") {
      // Shouldn't reach here because of the iosNoPwa branch above, but keep
      // a graceful path in case detection is wrong.
      onDismiss?.("not_now");
    } else {
      toast.error("Couldn't enable notifications. Try again later.");
      onDismiss?.(null);
    }
  };

  const handleNotNow = () => {
    trackSiteEvent("notif_prompt_dismissed", { reason: "not_now" });
    onDismiss?.("not_now");
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed z-30 left-3 right-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-6 sm:max-w-sm rounded-2xl border border-border bg-card/98 backdrop-blur-xl shadow-2xl p-4"
            data-testid="push-opt-in-prompt"
            role="dialog"
            aria-labelledby="push-opt-in-title"
          >
            <button
              type="button"
              onClick={handleNotNow}
              className="absolute top-2 right-2 h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition"
              aria-label="Dismiss"
              data-testid="push-prompt-close"
            >
              <X weight="bold" className="h-3.5 w-3.5" />
            </button>

            <div className="flex items-start gap-3 pr-6">
              <div className="flex-none h-10 w-10 grid place-items-center rounded-full bg-primary/10 text-primary">
                <Bell weight="duotone" className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="push-opt-in-title" className="font-display font-bold text-base leading-snug" data-testid="push-prompt-title">
                  {iosNoPwa
                    ? "Add TruckTrackr to your Home Screen"
                    : "Want alerts when food trucks are near you?"}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {iosNoPwa
                    ? "iPhone needs the app on your Home Screen first to send pings. Takes 10 seconds — tap Show me how."
                    : "We'll send a daily lunch heads-up and ping you when a truck goes live. Off-hours and weekends are quiet by default."}
                </p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleNotNow}
                disabled={busy}
                className="h-9 px-3 rounded-full text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-50"
                data-testid="push-prompt-not-now"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleEnable}
                disabled={busy}
                className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="push-prompt-enable"
              >
                <Lightning weight="duotone" className="h-3.5 w-3.5" />
                {iosNoPwa ? "Show me how" : busy ? "Enabling…" : "Enable alerts"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <IosInstallSheet
        open={iosSheetOpen}
        onClose={() => setIosSheetOpen(false)}
      />
    </>
  );
}
